import type {
  Action,
  ActionExample,
  IAgentRuntime,
  Memory,
  State,
  HandlerCallback,
  Content,
} from '@elizaos/core';
import { parseKeyValueXml, ModelType, logger } from '@elizaos/core';
import type {
  GetCrossChainAssetsContent,
  CrossChainAsset,
  CrossChainAssetNetwork,
  BridgeSupport,
  ZapperConfig,
} from '../types';
import { ZapperGraphQLClient, ZapperGraphQLUtils } from '../utils/zapperGraphQL';

// ✅ ADD standardized network mapping
const NETWORK_CHAIN_MAP: { [key: string]: number } = {
  ethereum: 1,
  polygon: 137,
  arbitrum: 42161,
  optimism: 10,
  base: 8453,
  avalanche: 43114,
  bsc: 56,
  fantom: 250,
  mainnet: 1,
  eth: 1,
  matic: 137,
  arb: 42161,
  op: 10,
  avax: 43114,
  bnb: 56,
  ftm: 250,
  'binance-smart-chain': 56,
};

// ✅ REPLACE complex address extraction with this simple version:
function extractWalletAddress(text: string, state?: State): string | null {
  logger.debug('🔍 Extracting wallet address', {
    textLength: text?.length || 0,
    hasState: !!state,
  });

  if (!text) return null;

  // Simple but reliable patterns for address extraction
  const patterns = [
    // Ethereum address: 0x followed by 40 hex characters
    /0x[a-fA-F0-9]{40}/,
    // ENS name: word characters, hyphens, followed by .eth
    /\b[\w-]+\.eth\b/,
    // Basename: word characters, hyphens, followed by .base
    /\b[\w-]+\.base\b/,
  ];

  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match) {
      logger.info('✅ Address found', {
        address: match[0],
        pattern: pattern.source,
      });
      return match[0];
    }
  }

  logger.debug('❌ No address found in text');
  return null;
}

// ✅ ADD proper type guard
function isGetCrossChainAssetsContent(
  _runtime: IAgentRuntime,
  content: unknown
): content is GetCrossChainAssetsContent {
  logger.debug('🔍 Starting content validation', {
    content,
    contentType: typeof content,
    isNull: content === null,
    isUndefined: content === undefined,
  });

  // Check if content is object and not null
  if (typeof content !== 'object' || content === null) {
    logger.debug('❌ Content validation failed: not an object or is null', {
      type: typeof content,
      isNull: content === null,
    });
    return false;
  }

  const contentObj = content as Record<string, unknown>;

  // Check if address exists and is a string
  if (typeof contentObj.address !== 'string') {
    logger.debug('❌ Content validation failed: address not a string', {
      hasAddress: 'address' in contentObj,
      addressType: typeof contentObj.address,
      address: contentObj.address,
    });
    return false;
  }

  // Check if address has content
  if (contentObj.address.length === 0) {
    logger.debug('❌ Content validation failed: address is empty string', {
      address: contentObj.address,
      length: contentObj.address.length,
    });
    return false;
  }

  logger.debug('✅ Content validation passed', {
    address: contentObj.address,
    addressLength: contentObj.address.length,
    hasNetworks: 'networks' in contentObj,
    networks: contentObj.networks,
    otherFields: Object.keys(contentObj).filter((k) => k !== 'address'),
  });

  return true;
}

const getCrossChainAssetsTemplate = `Respond with an XML block containing only the extracted values. Use key-value pairs.

{{recentMessages}}

Based on the user's message, extract cross-chain asset tracking request details:

Extract the following information:
- address: The wallet address to track assets for
- networks: Comma-separated list of networks to check (optional)
- includeBalances: Whether to include token balances (true/false)
- includeBridgeSupport: Whether to include bridge support information (true/false)
- includePriceData: Whether to include price data (true/false)
- minBalance: Minimum balance threshold in USD (optional)
- sortBy: Sort results by (balance, value, network, name)
- limit: Maximum number of assets to return (optional)

Respond with an XML block containing only the extracted values.`;

export const getCrossChainAssetsAction: Action = {
  name: 'GET_CROSS_CHAIN_ASSETS',
  similes: [
    'GET_CROSS_CHAIN_ASSETS',
    'GET_MULTI_CHAIN_ASSETS',
    'CROSS_CHAIN_ASSETS',
    'MULTI_CHAIN_ASSETS',
    'CROSS_CHAIN_BALANCE',
    'MULTI_CHAIN_BALANCE',
    'CROSS_CHAIN_PORTFOLIO',
    'MULTI_CHAIN_PORTFOLIO',
    'BRIDGE_ASSETS',
    'CHAIN_ASSETS',
    'CROSS_CHAIN_TRACKING',
    'MULTI_CHAIN_TRACKING',
    'SHOW_CROSS_CHAIN_ASSETS',
    'CHECK_CROSS_CHAIN_ASSETS',
    'VIEW_CROSS_CHAIN_ASSETS',
    'FETCH_CROSS_CHAIN_ASSETS',
  ],
  description:
    'Track assets across multiple blockchain networks with cross-chain asset aggregation and bridge support information',
  validate: async (runtime: IAgentRuntime, message: Memory, state?: State): Promise<boolean> => {
    logger.info('🔍 Starting GET_CROSS_CHAIN_ASSETS validation');

    // Check API key
    const apiKey = runtime.getSetting('ZAPPER_API_KEY');
    if (!apiKey) {
      logger.error('❌ ZAPPER_API_KEY not configured');
      return false;
    }

    // Check for required data (address)
    const messageText = message.content.text || '';
    const address = extractWalletAddress(messageText, state);

    if (!address) {
      logger.warn('⚠️ No wallet address found for GET_CROSS_CHAIN_ASSETS');
      return false;
    }

    logger.info('✅ Validation passed', {
      address,
      dataType: 'wallet_address',
    });

    return true;
  },
  handler: async (
    runtime: IAgentRuntime,
    message: Memory,
    state?: State,
    options?: { [key: string]: unknown },
    callback?: HandlerCallback
  ): Promise<boolean> => {
    logger.info('🚀 Starting GET_CROSS_CHAIN_ASSETS handler');

    try {
      // 1. Get configuration
      const apiKey = runtime.getSetting('ZAPPER_API_KEY');
      const apiUrl = runtime.getSetting('ZAPPER_API_URL') || 'https://public.zapper.xyz/graphql';

      if (!apiKey) {
        throw new Error('ZAPPER_API_KEY not configured');
      }

      // 2. Extract required data
      const messageText = message.content.text || '';
      const address = extractWalletAddress(messageText, state);

      if (!address) {
        const helpMessage = `I need a wallet address to track cross-chain assets. Please provide:

• Ethereum address (e.g., 0x1234...5678)
• ENS name (e.g., vitalik.eth)
• Basename (e.g., user.base)

Try: "Show my cross-chain assets for 0x1234567890123456789012345678901234567890"`;

        callback?.({
          text: helpMessage,
          content: { error: 'No wallet address provided' },
        });
        return false;
      }

      logger.info('📍 Using address for lookup', { address });

      // 3. Initialize GraphQL client
      const client = new ZapperGraphQLClient({
        apiKey,
        apiUrl,
        debug: true,
        timeout: 30000,
        retryAttempts: 3,
      });

      // 4. Compose state for prompt
      let currentState: State;
      if (state) {
        currentState = state;
      } else {
        currentState = await runtime.composeState(message, ['RECENT_MESSAGES']);
      }

      // 5. Generate AI prompt for parameter extraction
      const prompt = getCrossChainAssetsTemplate.replace(
        '{{recentMessages}}',
        currentState.recentMessagesData || ''
      );

      const result = await runtime.useModel(ModelType.TEXT_SMALL, {
        prompt,
        stopSequences: [],
      });

      const content = parseKeyValueXml(result);

      // Validate the content with address fallback
      let validContent: GetCrossChainAssetsContent;
      if (isGetCrossChainAssetsContent(runtime, content)) {
        validContent = content;
      } else {
        // Fallback with extracted address
        validContent = {
          address,
          networks: '',
          includeBalances: true,
          includeBridgeSupport: true,
          includePriceData: true,
        };
      }

      // 6. Process parameters safely
      const includeBalances =
        validContent.includeBalances === true || validContent.includeBalances === 'true';
      const includeBridgeSupport =
        validContent.includeBridgeSupport === true || validContent.includeBridgeSupport === 'true';
      const includePriceData =
        validContent.includePriceData === true || validContent.includePriceData === 'true';
      const minBalance =
        typeof validContent.minBalance === 'string'
          ? Number.parseFloat(validContent.minBalance)
          : validContent.minBalance;
      const limit =
        typeof validContent.limit === 'string'
          ? Number.parseInt(validContent.limit, 10)
          : validContent.limit;

      // Parse networks
      let networks: string[] = [];
      if (validContent.networks) {
        if (typeof validContent.networks === 'string') {
          networks = validContent.networks.split(',').map((n) => n.trim());
        } else if (Array.isArray(validContent.networks)) {
          networks = validContent.networks;
        }
      }

      // 7. Prepare GraphQL query and variables
      const chainIds =
        networks.length > 0
          ? networks.map((network) => {
              const normalized = network.toLowerCase().trim();
              return NETWORK_CHAIN_MAP[normalized] || 1;
            })
          : [1, 137, 42161, 10, 8453]; // Default to major networks

      const query = `
        query PortfolioV2($addresses: [Address!]!, $chainIds: [Int!]) {
          portfolioV2(addresses: $addresses, chainIds: $chainIds) {
            tokenBalances {
              byToken {
                edges {
                  node {
                    balance
                    balanceUSD
                    symbol
                    name
                    network {
                      name
                    }
                    tokenAddress
                  }
                }
              }
            }
          }
        }
      `;

      const variables = {
        addresses: [validContent.address],
        chainIds: chainIds,
      };

      logger.info('📡 Making GraphQL request to Zapper API');

      // 8. Execute query using GraphQL client
      const response = await client.query(query, variables);

      if (ZapperGraphQLUtils.hasErrors(response)) {
        const errorMessage = ZapperGraphQLUtils.formatGraphQLErrors(response.errors || []);
        logger.error('❌ GraphQL errors in response', { errors: response.errors });
        throw new Error(`GraphQL errors: ${errorMessage}`);
      }

      // 9. Process response data safely
      const responseData = response.data?.portfolioV2?.tokenBalances?.byToken?.edges;

      if (!responseData || (Array.isArray(responseData) && responseData.length === 0)) {
        logger.warn('⚠️ No data found', { address });

        callback?.({
          text: `No cross-chain assets found for address ${validContent.address}.`,
          content: {
            address: validContent.address,
            message: 'No data found',
            crossChainAssets: [],
          },
        });
        return true;
      }

      logger.info('✅ Data found, processing results', {
        resultCount: Array.isArray(responseData) ? responseData.length : 1,
      });

      // 10. Format the response data
      const formattedResponse = processResponseData(responseData, validContent.address, {
        includeBalances,
        includeBridgeSupport,
        includePriceData,
        minBalance,
        sortBy: validContent.sortBy,
        limit,
      });

      logger.info('✅ Response formatted successfully', {
        address: formattedResponse.address,
        assetCount: formattedResponse.crossChainAssets.length,
        totalValue: formattedResponse.totalValue,
      });

      const responseText = generateResponseText(formattedResponse);

      callback?.({
        text: responseText,
        content: formattedResponse,
      });

      logger.info('✅ GET_CROSS_CHAIN_ASSETS handler completed successfully');
      return true;
    } catch (error) {
      logger.error('❌ Error in GET_CROSS_CHAIN_ASSETS handler', {
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      });

      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';

      callback?.({
        text: `Unable to get cross-chain assets: ${errorMessage}`,
        content: { error: errorMessage },
      });

      return false;
    }
  },
  examples: [
    // Successful case with full data
    [
      {
        name: '{{user1}}',
        content: {
          text: 'Show me my cross-chain assets for 0x1234567890abcdef1234567890abcdef12345678',
        },
      },
      {
        name: '{{agent}}',
        content: {
          text: 'Found 5 cross-chain assets with total value of $12,450.67 across 3 networks',
          action: 'GET_CROSS_CHAIN_ASSETS',
        },
      },
    ],
    // ENS case
    [
      {
        name: '{{user1}}',
        content: {
          text: 'Track my multi-chain portfolio for vitalik.eth',
        },
      },
      {
        name: '{{agent}}',
        content: {
          text: 'Found 8 cross-chain assets for vitalik.eth with total value of $25,890.45',
          action: 'GET_CROSS_CHAIN_ASSETS',
        },
      },
    ],
    // Missing data case
    [
      {
        name: '{{user1}}',
        content: {
          text: 'Show my cross-chain assets',
        },
      },
      {
        name: '{{agent}}',
        content: {
          text: 'I need a wallet address to track cross-chain assets. Please provide an Ethereum address, ENS name, or Basename.',
          action: 'GET_CROSS_CHAIN_ASSETS',
        },
      },
    ],
    // Bridge support request
    [
      {
        name: '{{user1}}',
        content: {
          text: 'Get my cross-chain assets with bridge support for 0xabcd...1234',
        },
      },
      {
        name: '{{agent}}',
        content: {
          text: 'Found 6 bridgeable cross-chain assets with comprehensive bridge support information',
          action: 'GET_CROSS_CHAIN_ASSETS',
        },
      },
    ],
    // Specific networks
    [
      {
        name: '{{user1}}',
        content: {
          text: 'Show assets across Ethereum, Polygon, and Arbitrum for user.base',
        },
      },
      {
        name: '{{agent}}',
        content: {
          text: 'Found 4 cross-chain assets across Ethereum, Polygon, and Arbitrum networks',
          action: 'GET_CROSS_CHAIN_ASSETS',
        },
      },
    ],
  ] as ActionExample[][],
};

// ✅ Safe data processing with proper types
interface ProcessOptions {
  includeBalances: boolean;
  includeBridgeSupport: boolean;
  includePriceData: boolean;
  minBalance?: number;
  sortBy?: string;
  limit?: number;
}

interface TokenNode {
  balance: string;
  balanceUSD: number;
  symbol: string;
  name: string;
  network?: { name: string };
  tokenAddress: string;
}

function processResponseData(
  data: Array<{ node: TokenNode }>,
  address: string,
  options: ProcessOptions
): {
  address: string;
  totalValue: number;
  assetCount: number;
  uniqueNetworks: number;
  crossChainAssets: CrossChainAsset[];
} {
  const results: CrossChainAsset[] = [];
  let totalValue = 0;
  const uniqueNetworks = new Set<string>();

  // Group tokens by symbol to create cross-chain assets
  const tokensBySymbol: { [symbol: string]: TokenNode[] } = {};

  for (const edge of data) {
    if (!edge?.node) continue;

    const symbol = edge.node.symbol;
    if (!tokensBySymbol[symbol]) {
      tokensBySymbol[symbol] = [];
    }
    tokensBySymbol[symbol].push(edge.node);
  }

  // Convert to CrossChainAsset format
  for (const [symbol, tokens] of Object.entries(tokensBySymbol)) {
    const firstToken = tokens[0];
    const totalBalance = tokens.reduce((sum, token) => sum + Number.parseFloat(token.balance), 0);
    const totalBalanceUSD = tokens.reduce((sum, token) => sum + token.balanceUSD, 0);

    const networks: CrossChainAssetNetwork[] = tokens.map((token) => {
      const networkName = token.network?.name || 'Unknown';
      uniqueNetworks.add(networkName);

      return {
        networkId: networkName.toLowerCase(),
        networkName,
        contractAddress: token.tokenAddress,
        balance: Number.parseFloat(token.balance),
        balanceUSD: token.balanceUSD,
        bridgeable: true,
      };
    });

    const asset: CrossChainAsset = {
      symbol,
      name: firstToken.name,
      totalBalance,
      totalBalanceUSD,
      networks,
      bridgeSupport: options.includeBridgeSupport ? getBridgeSupport(symbol, networks) : [],
      priceData: {
        price: totalBalance > 0 ? totalBalanceUSD / totalBalance : 0,
        priceUSD: totalBalance > 0 ? totalBalanceUSD / totalBalance : 0,
        change24h: 0,
      },
    };

    totalValue += totalBalanceUSD;
    results.push(asset);
  }

  let filteredAssets = results;

  // Apply minimum balance filter
  if (options.minBalance !== undefined) {
    const minBalanceThreshold = options.minBalance;
    filteredAssets = filteredAssets.filter((asset) => asset.totalBalanceUSD >= minBalanceThreshold);
  }

  // Apply sorting
  if (options.sortBy) {
    switch (options.sortBy.toLowerCase()) {
      case 'balance':
        filteredAssets.sort((a, b) => b.totalBalance - a.totalBalance);
        break;
      case 'value':
        filteredAssets.sort((a, b) => b.totalBalanceUSD - a.totalBalanceUSD);
        break;
      case 'network':
        filteredAssets.sort((a, b) => b.networks.length - a.networks.length);
        break;
      case 'name':
        filteredAssets.sort((a, b) => a.name.localeCompare(b.name));
        break;
    }
  }

  // Apply limit
  if (options.limit) {
    filteredAssets = filteredAssets.slice(0, options.limit);
  }

  return {
    address,
    totalValue,
    assetCount: filteredAssets.length,
    uniqueNetworks: uniqueNetworks.size,
    crossChainAssets: filteredAssets,
  };
}

// ✅ Response text generation
function generateResponseText(data: {
  address: string;
  totalValue: number;
  assetCount: number;
  uniqueNetworks: number;
  crossChainAssets: CrossChainAsset[];
}): string {
  if (data.assetCount === 0) {
    return `No cross-chain assets found for ${data.address}`;
  }

  let response = '**Cross-Chain Assets Portfolio**\n\n';

  response += '**Portfolio Summary:**\n';
  response += `- Total Assets: ${data.assetCount}\n`;
  response += `- Total Value: $${data.totalValue.toLocaleString()}\n`;
  response += `- Active Networks: ${data.uniqueNetworks}\n\n`;

  for (const asset of data.crossChainAssets) {
    response += `**${asset.symbol} (${asset.name})**\n`;
    response += `- Total Balance: ${asset.totalBalance.toLocaleString()} ${asset.symbol} ($${asset.totalBalanceUSD.toLocaleString()})\n`;

    if (asset.priceData) {
      const changeSymbol = asset.priceData.change24h >= 0 ? '+' : '';
      response += `- Price: $${asset.priceData.priceUSD.toLocaleString()} (${changeSymbol}${asset.priceData.change24h.toFixed(2)}% 24h)\n`;
    }

    response += '- Networks:\n';
    for (const network of asset.networks) {
      response += `  • ${network.networkName}: ${network.balance.toLocaleString()} ${asset.symbol} ($${network.balanceUSD.toLocaleString()})`;
      if (network.bridgeable) {
        response += ' ✅ Bridgeable';
      }
      response += '\n';
    }

    if (asset.bridgeSupport && asset.bridgeSupport.length > 0) {
      response += '- Bridge Support:\n';
      for (const bridge of asset.bridgeSupport) {
        response += `  • ${bridge.protocol}: ${bridge.fromNetworks.join(', ')} ↔ ${bridge.toNetworks.join(', ')}\n`;
        if (bridge.timeEstimate) {
          response += `    Time: ${bridge.timeEstimate}`;
        }
        if (bridge.fees.fixed || bridge.fees.percentage) {
          response += `, Fees: ${bridge.fees.fixed || 0}${bridge.fees.percentage ? ` + ${bridge.fees.percentage}%` : ''}`;
        }
        response += '\n';
      }
    }

    response += '\n';
  }

  response += '**Bridge Recommendations:**\n';
  response += '- Use official bridges for better security\n';
  response += '- Consider gas costs when moving smaller amounts\n';
  response += '- Monitor network congestion for optimal timing\n';
  response += '- Batch multiple transfers when possible';

  return response;
}

function getBridgeSupport(symbol: string, networks: CrossChainAssetNetwork[]): BridgeSupport[] {
  // Define known bridge supports for common tokens
  const bridgeSupports: { [key: string]: BridgeSupport[] } = {
    USDC: [
      {
        protocol: 'Circle CCTP',
        fromNetworks: ['ethereum', 'arbitrum', 'optimism', 'base', 'polygon'],
        toNetworks: ['ethereum', 'arbitrum', 'optimism', 'base', 'polygon'],
        supported: true,
        fees: { fixed: 0, percentage: 0.1 },
      },
    ],
    WETH: [
      {
        protocol: 'Arbitrum Bridge',
        fromNetworks: ['ethereum'],
        toNetworks: ['arbitrum'],
        supported: true,
        fees: { fixed: 0.001, gas: 0.01 },
      },
    ],
  };

  return bridgeSupports[symbol] || [];
}
